import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthSession } from "../auth/shared";

/**
 * QRScannerLayout - Layout wrapper for QR Scanner nested routes
 * Routes:
 *   /qr-scanner → redirects to /login or /welcome based on auth
 *   /qr-scanner/login → LoginPage
 *   /qr-scanner/welcome → QRScannerWelcome
 *   /qr-scanner/event/:id/camera → QRScannerCameraTab
 *   /qr-scanner/event/:id/attendees → ScannedAttendeesTab
 * 
 * Protection:
 *   - /qr-scanner/event (without valid :id) → redirects to /welcome
 *   - Invalid routes → redirects to /welcome or /login
 */
const QRScannerLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, isInitialized } = useAuthSession();

  useEffect(() => {
    if (!isInitialized) return;

    const path = location.pathname;

    // Handle base /qr-scanner route
    if (path === "/qr-scanner" || path === "/qr-scanner/") {
      navigate(isLoggedIn ? "/qr-scanner/welcome" : "/qr-scanner/login", { replace: true });
      return;
    }

    // Protect against invalid /qr-scanner/event routes (without valid event ID)
    // Valid: /qr-scanner/event/123/camera, /qr-scanner/event/abc-def/attendees
    // Invalid: /qr-scanner/event, /qr-scanner/event/
    const isInvalidEventRoute = /^\/qr-scanner\/event\/?$/.test(path);
    if (isInvalidEventRoute) {
      navigate(isLoggedIn ? "/qr-scanner/welcome" : "/qr-scanner/login", { replace: true });
      return;
    }

    // Redirect unauthenticated users trying to access protected routes
    if (!isLoggedIn && path !== "/qr-scanner/login") {
      navigate("/qr-scanner/login", { replace: true });
      return;
    }

    // Redirect authenticated users away from login page
    if (isLoggedIn && path === "/qr-scanner/login") {
      navigate("/qr-scanner/welcome", { replace: true });
      return;
    }
  }, [location.pathname, isLoggedIn, isInitialized, navigate]);

  // Show nothing while auth is initializing
  if (!isInitialized) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C49A]"></div>
      </div>
    );
  }

  return (
    <div className="h-screen">
      <Outlet />
    </div>
  );
};

export default QRScannerLayout;
